import type { Booking, FormAnswer, FormSubmitResult } from "./types";
import { cleanText, sanitizeJsonValue } from "./utils";

const baseUrl = String(import.meta.env.VITE_N8N_WEBHOOK_BASE_URL || "").replace(/\/$/, "");
const bookingPath = String(import.meta.env.VITE_N8N_BOOKING_PATH || "mjs-multi-book").replace(/^\//, "");
const cancelPath = String(import.meta.env.VITE_N8N_CANCEL_PATH || "mjs-multi-cancel").replace(/^\//, "");
const busySlotsPath = String(import.meta.env.VITE_N8N_BUSY_SLOTS_PATH || "mjs-multi-busy-slots").replace(/^\//, "");
const submitFormPath = String(import.meta.env.VITE_N8N_FORM_SUBMIT_PATH || "mjs-multi-submit-form").replace(/^\//, "");

export const n8nConfigured = Boolean(baseUrl);

function endpoint(path: string): string {
  return `${baseUrl}/${path}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const raw = (await response.text()).replace(/^\uFEFF/, "").trim();
  let body: Record<string, unknown> = {};

  if (raw) {
    try {
      body = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      body = { message: raw };
      if (response.ok) console.warn("n8n returned a successful non-JSON response.", raw);
    }
  }

  if (!response.ok) {
    const message = typeof body.message === "string" && body.message.trim()
      ? body.message
      : `Request failed (${response.status})`;
    const error = new Error(message) as Error & { code?: string; status?: number };
    error.code = typeof body.code === "string" ? body.code : undefined;
    error.status = response.status;
    throw error;
  }

  return body as T;
}

export async function createRemoteBooking(
  hostSlug: string,
  eventSlug: string,
  payload: Omit<Booking, "id" | "createdAt" | "status" | "hostCancelToken">
): Promise<Booking | null> {
  if (!n8nConfigured) return null;
  const requestBody = sanitizeJsonValue({
    ...payload,
    hostSlug,
    eventSlug,
    idempotencyKey: crypto.randomUUID(),
    requestedAt: new Date().toISOString(),
    pageOrigin: window.location.origin
  });
  const response = await fetch(endpoint(bookingPath), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(requestBody)
  });
  const result = await parseResponse<Partial<Booking>>(response);
  const fallbackId = cleanText(result.id || result.bookingId) || `booking_${crypto.randomUUID().replaceAll("-", "").slice(0, 16)}`;
  return {
    ...payload,
    ...result,
    id: cleanText(result.id || result.bookingId) || fallbackId,
    bookingId: cleanText(result.bookingId || result.id) || fallbackId,
    hostSlug: cleanText(result.hostSlug) || cleanText(hostSlug),
    status: result.status || "confirmed",
    createdAt: cleanText(result.createdAt) || new Date().toISOString()
  } as Booking;
}

export async function submitRemoteForm(
  hostSlug: string,
  formSlug: string,
  answers: Record<string, FormAnswer>,
  source: "standalone" | "inline" | "popup"
): Promise<FormSubmitResult | null> {
  if (!n8nConfigured) return null;
  const params = new URLSearchParams(window.location.search);
  const utm = Object.fromEntries(
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
      .map((key) => [key, params.get(key) || ""])
      .filter(([, value]) => Boolean(value))
  );
  const response = await fetch(endpoint(submitFormPath), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(sanitizeJsonValue({
      hostSlug,
      formSlug,
      answers,
      source,
      referrer: document.referrer || "",
      utm,
      idempotencyKey: crypto.randomUUID(),
      requestedAt: new Date().toISOString(),
      pageOrigin: window.location.origin
    }))
  });
  return parseResponse<FormSubmitResult>(response);
}

export async function cancelRemoteBooking(booking: Booking): Promise<boolean> {
  if (!n8nConfigured) return false;
  if (!booking.hostSlug || !booking.hostCancelToken) throw new Error("This booking is missing its cancellation credential.");
  const response = await fetch(endpoint(cancelPath), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      hostSlug: booking.hostSlug,
      bookingId: booking.bookingId || booking.id,
      hostCancelToken: booking.hostCancelToken,
      requestedAt: new Date().toISOString()
    })
  });
  await parseResponse<{ id: string; status: string }>(response);
  return true;
}

export async function listRemoteBusySlots(
  hostSlug: string,
  eventSlug: string,
  startAt: string,
  endAt: string
): Promise<Booking[] | null> {
  if (!n8nConfigured) return null;
  const url = new URL(endpoint(busySlotsPath));
  url.searchParams.set("hostSlug", hostSlug);
  url.searchParams.set("eventSlug", eventSlug);
  url.searchParams.set("startAt", startAt);
  url.searchParams.set("endAt", endAt);
  const response = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    cache: "no-store"
  });
  const result = await parseResponse<{ bookings?: Booking[] }>(response);
  return result.bookings ?? [];
}

export function buildGoogleCalendarAuthorizationUrl(state: string, loginHint = ""): string {
  const clientId = String(import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || "");
  const redirectUri = String(import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI || "");
  if (!clientId || !redirectUri) throw new Error("Google Calendar OAuth is not configured in the frontend environment.");
  const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("access_type", "offline");
  url.searchParams.set("prompt", "consent select_account");
  url.searchParams.set("include_granted_scopes", "true");
  url.searchParams.set("state", state);
  url.searchParams.set("scope", [
    "openid",
    "email",
    "https://www.googleapis.com/auth/calendar.events",
    "https://www.googleapis.com/auth/calendar.readonly"
  ].join(" "));
  if (loginHint) url.searchParams.set("login_hint", loginHint);
  return url.toString();
}
